import sdk from '../../../sdk';
import l18n from '../../../shared/strings';
import * as utils from '../../../shared/utils';
import Logger from '../../../services/Logger';
import { showErrorDialog } from '../../dialog';

const IMAGE_PICKED = 'imagePicked';

export const IMAGE_FIELDS = ['logoUrl', 'avatarUrl', 'backgroundLoginPageUrl', 'thumbnailUrl'];

export const hasImageUrl = (url) => Boolean(url && url !== IMAGE_PICKED);

export const stripWebsiteImageFields = (website = {}) => {
  const payload = { ...website };
  IMAGE_FIELDS.forEach((field) => {
    if (payload[field]) {
      payload[field] = '';
    }
  });
  return payload;
};

const IMAGE_UPLOAD_METHODS = {
  avatarUrl: sdk.websites.uploadAvatar,
  backgroundLoginPageUrl: sdk.websites.uploadBackground,
  thumbnailUrl: sdk.websites.uploadThumbnail,
  logoUrl: sdk.websites.uploadLogo,
};

export const getUploadErrorMessage = (failedFields, firstError) => {
  const apiMessage = utils.getDataFromResponceError(firstError, 'msg');
  if (apiMessage) {
    return apiMessage;
  }

  const fieldLabels = failedFields
    .map((field) => l18n.WEBSITES.websiteImageFieldLabels[field])
    .filter(Boolean)
    .join(', ');

  return fieldLabels
    ? l18n.WEBSITES.textWebsiteImageFieldsUnsuccessful(fieldLabels)
    : l18n.WEBSITES.textWebsiteSomeImagesUnsuccessful;
};

const uploadImagesForWebsite = async ({ websiteId, settings, silent = false }) => {
  const uploadedImages = {};
  const failedFields = [];

  const fieldsToUpload = Object.keys(IMAGE_UPLOAD_METHODS)
    .filter((field) => hasImageUrl(settings?.[field]));

  if (!fieldsToUpload.length) {
    return { uploadedImages, hasError: false, failedFields };
  }

  const uploadResults = await Promise.allSettled(
    fieldsToUpload.map(async (field) => {
      const { data: { url } } = await IMAGE_UPLOAD_METHODS[field](websiteId, settings[field]);
      return { field, url };
    }),
  );

  uploadResults.forEach((result, index) => {
    const field = fieldsToUpload[index];

    if (result.status === 'fulfilled') {
      uploadedImages[result.value.field] = result.value.url;
      return;
    }

    failedFields.push(field);
    Logger.error(new Error('Error uploading website image'), { error: result.reason }, [
      'UploadWebsiteImageFailed',
      {
        errorMessage: (result.reason && result.reason.message) || 'NoMessage',
        websiteId,
        field,
      },
    ]);
  });

  const hasError = failedFields.length > 0;
  const firstRejected = uploadResults.find((result) => result.status === 'rejected');

  if (hasError && !silent) {
    showErrorDialog(getUploadErrorMessage(failedFields, firstRejected?.reason));
  }

  return {
    uploadedImages,
    hasError,
    failedFields,
    firstError: firstRejected?.reason,
  };
};

export default uploadImagesForWebsite;
